import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { ChevronDown, ChevronUp, Send, Check, AlertTriangle, Lock, Wrench, X, Plus } from 'lucide-react';
import { FridgeTempsSection } from './FridgeTemps';

const C = {
  yellow:'#F7EC71', yellowLight:'#FEFCE8', orange:'#E4AC49', orangeLight:'#FDF3DC',
  green:'#7EA594', greenLight:'#EAF2EF', cream:'#F7F6EF', charcoal:'#2F3A42',
  navy:'#445366', mid:'#7A8A96', border:'#D8DEE3', skyBlue:'#BCD9E5',
  skyBlueLight:'#EAF4F8', skyText:'#3A8FA3', white:'#FFFFFF',
  red:'#DC3545', redLight:'#FFF0F0', amber:'#E07B00', amberLight:'#FFF3E0',
  pink:'#F2B8CB', pinkLight:'#FDF0F5', pinkActive:'#C4607A',
  purple:'#7C3AED', purpleLight:'#F3EEFF',
};

const VENUE_IDENTITY = {
  'Prep Kitchen': { bg: C.cream,    text: C.navy,    border: C.border,  short: 'Prep Kitchen' },
  'Sassys':       { bg: '#EDE0F7',  text: '#6D28A0', border: '#6D28A0', short: 'Sassys' },
};



const ALLOWED_ROLES        = ['admin', 'manager', 'supervisor', 'kitchen'];
const VENUES_WITH_KITCHEN  = ['Prep Kitchen', 'Sassys'];

const PRIORITY_CFG = {
  normal: { label: 'Normal', color: C.navy, bg: '#eef1f5' },
  urgent: { label: 'Urgent', color: C.red,  bg: C.redLight },
};

const MAINT_CATEGORIES = [
  { key: 'Equipment', color: C.skyText, bg: C.skyBlueLight },
  { key: 'Building',  color: C.amber,   bg: C.amberLight   },
  { key: 'Safety',    color: C.red,     bg: C.redLight      },
  { key: 'Other',     color: C.mid,     bg: C.cream         },
];

const KITCHEN_PROMPTS = [
  { key: 'service_notes',     label: 'Service',        placeholder: 'How did service go from the kitchen? Flow, timing, pressure points?' },
  { key: 'staff_notes',       label: 'Staff',          placeholder: 'Any lates, no-shows, performance notes?' },
  { key: 'stock_notes',       label: 'Stock & Wastage', placeholder: "Anything 86'd, wasted, or unexpectedly low?" },
  { key: 'recognition_notes', label: 'Recognition',    placeholder: 'Who did well today?' },
  { key: 'equipment_notes',   label: 'Equipment',      placeholder: 'Any issues with equipment today? Note here — escalate below if needed.' },
];

function getToday() { return new Date().toISOString().split('T')[0]; }

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}
function formatTime(isoStr) {
  if (!isoStr) return '';
  return new Date(isoStr).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

// ─── Maintenance mini-modal ───────────────────────────────────────────────────
function MaintenanceModal({ venue, currentUser, onClose, onCreated }) {
  const [category,    setCategory]    = useState('Equipment');
  const [priority,    setPriority]    = useState('normal');
  const [title,       setTitle]       = useState('');
  const [description, setDescription] = useState('');
  const [submitting,  setSubmitting]  = useState(false);
  const [error,       setError]       = useState('');

  const canSubmit = title.trim() && category;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    const { data, error: dbErr } = await supabase.from('maintenance_tasks').insert({
      venue,
      category,
      priority,
      area: 'Kitchen',
      title: title.trim(),
      description: description.trim() || null,
      reported_by: currentUser?.name || 'unknown',
      reported_at: new Date().toISOString(),
      status: 'open',
    }).select().single();
    setSubmitting(false);
    if (dbErr) { setError('Failed to log. Try again.'); return; }
    if (data && onCreated) onCreated(data);
    onClose();
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(47,58,66,0.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.25rem' }}
      onClick={onClose}
    >
      <div
        style={{ background: C.white, borderRadius: '16px', padding: '1.5rem', width: '100%', maxWidth: '420px', boxShadow: '0 12px 40px rgba(0,0,0,0.2)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem' }}>
            <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: C.amberLight, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Wrench size={15} color={C.amber} />
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '0.92rem', fontWeight: 800, color: C.charcoal }}>Log Maintenance Issue</p>
              <p style={{ margin: 0, fontSize: '0.74rem', color: C.mid }}>{venue}</p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mid, padding: '0.2rem' }}>
            <X size={16} />
          </button>
        </div>

        {/* Category */}
        <div style={{ marginBottom: '0.85rem' }}>
          <label style={{ display: 'block', fontSize: '0.67rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.35rem' }}>Category</label>
          <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
            {MAINT_CATEGORIES.map(cat => (
              <button key={cat.key} onClick={() => setCategory(cat.key)} style={{
                padding: '0.3rem 0.75rem', borderRadius: '999px',
                border: `1.5px solid ${category === cat.key ? cat.color : C.border}`,
                background: category === cat.key ? cat.bg : C.white,
                color: category === cat.key ? cat.color : C.mid,
                fontSize: '0.78rem', fontWeight: category === cat.key ? 700 : 400, cursor: 'pointer',
              }}>{cat.key}</button>
            ))}
          </div>
        </div>

        {/* Priority */}
        <div style={{ marginBottom: '0.85rem' }}>
          <label style={{ display: 'block', fontSize: '0.67rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.35rem' }}>Priority</label>
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            {Object.entries(PRIORITY_CFG).map(([key, cfg]) => (
              <button key={key} onClick={() => setPriority(key)} style={{
                flex: 1, padding: '0.4rem 0', borderRadius: '8px',
                border: `2px solid ${priority === key ? cfg.color : C.border}`,
                background: priority === key ? cfg.bg : C.white,
                color: priority === key ? cfg.color : C.mid,
                fontSize: '0.82rem', fontWeight: priority === key ? 700 : 400, cursor: 'pointer',
              }}>{cfg.label}</button>
            ))}
          </div>
        </div>

        {/* Title */}
        <div style={{ marginBottom: '0.85rem' }}>
          <label style={{ display: 'block', fontSize: '0.67rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.35rem' }}>Title *</label>
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="e.g. Oven thermostat not holding temp"
            style={{ width: '100%', padding: '0.5rem 0.7rem', border: `1.5px solid ${title.trim() ? C.border : C.amber + '88'}`, borderRadius: '8px', fontSize: '0.88rem', color: C.charcoal, background: C.white, boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' }}
          />
        </div>

        {/* Description */}
        <div style={{ marginBottom: '1.1rem' }}>
          <label style={{ display: 'block', fontSize: '0.67rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.35rem' }}>Details (optional)</label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Any extra context — when it started, what was tried, etc."
            rows={3}
            style={{ width: '100%', padding: '0.42rem 0.6rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.84rem', resize: 'vertical', boxSizing: 'border-box', color: C.charcoal, background: C.cream, fontFamily: 'inherit' }}
          />
        </div>

        {error && <p style={{ margin: '0 0 0.65rem', fontSize: '0.8rem', color: C.red }}>{error}</p>}

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={onClose} style={{ flex: 1, padding: '0.6rem', border: `1.5px solid ${C.border}`, borderRadius: '8px', background: C.white, color: C.mid, fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer' }}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            style={{ flex: 1, padding: '0.6rem', border: 'none', borderRadius: '8px', background: canSubmit && !submitting ? C.amber : C.border, color: C.white, fontWeight: 800, fontSize: '0.88rem', cursor: canSubmit && !submitting ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}
          >
            {submitting ? 'Logging…' : <><Wrench size={13} /> Log Issue</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Confirm submit modal ─────────────────────────────────────────────────────
function ConfirmModal({ shift, venue, managerName, filledCount, onConfirm, onCancel, submitting }) {
  const vi = VENUE_IDENTITY[venue] || {};
  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(47,58,66,0.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.25rem' }}
      onClick={onCancel}
    >
      <div
        style={{ background: C.white, borderRadius: '16px', padding: '1.5rem', width: '100%', maxWidth: '380px', boxShadow: '0 12px 40px rgba(0,0,0,0.2)' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', marginBottom: '1rem' }}>
          <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: C.orangeLight, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Send size={16} color={C.orange} />
          </div>
          <div>
            <p style={{ margin: 0, fontSize: '0.95rem', fontWeight: 800, color: C.charcoal }}>Submit kitchen report?</p>
            <p style={{ margin: 0, fontSize: '0.78rem', color: C.mid }}>This cannot be undone</p>
          </div>
        </div>

        <div style={{ background: C.cream, borderRadius: '10px', padding: '0.85rem 1rem', marginBottom: '1.1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.83rem' }}>
            <span style={{ color: C.mid }}>Submitted by</span>
            <span style={{ fontWeight: 700, color: C.charcoal }}>{managerName}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.83rem' }}>
            <span style={{ color: C.mid }}>Venue</span>
            <span style={{ fontWeight: 700, color: vi.text || C.charcoal }}>{venue}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.83rem' }}>
            <span style={{ color: C.mid }}>Shift</span>
            <span style={{ fontWeight: 700, color: C.charcoal, textTransform: 'capitalize' }}>{shift}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.83rem' }}>
            <span style={{ color: C.mid }}>Fields filled</span>
            <span style={{ fontWeight: 700, color: filledCount === KITCHEN_PROMPTS.length ? C.green : filledCount === 0 ? C.red : C.amber }}>
              {filledCount} / {KITCHEN_PROMPTS.length}
            </span>
          </div>
        </div>

        {filledCount === 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.65rem 0.85rem', background: C.amberLight, border: `1px solid ${C.amber}44`, borderRadius: '8px', marginBottom: '1rem' }}>
            <AlertTriangle size={14} color={C.amber} />
            <span style={{ fontSize: '0.78rem', color: C.amber, fontWeight: 600 }}>No notes filled in — are you sure?</span>
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={onCancel} style={{ flex: 1, padding: '0.6rem', border: `1.5px solid ${C.border}`, borderRadius: '8px', background: C.white, color: C.mid, fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer' }}>
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={submitting}
            style={{ flex: 1, padding: '0.6rem', border: 'none', borderRadius: '8px', background: submitting ? C.border : C.orange, color: C.white, fontWeight: 800, fontSize: '0.88rem', cursor: submitting ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}
          >
            {submitting ? 'Submitting…' : <><Send size={13} /> Confirm</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Kitchen shift card (history) ────────────────────────────────────────────
function KitchenCard({ entry, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const filled  = KITCHEN_PROMPTS.filter(p => entry[p.key]);
  const isToday = entry.date === getToday();

  return (
    <div style={{
      background: C.white, borderRadius: '12px',
      border: `1px solid ${isToday ? `${C.orange}55` : C.border}`,
      overflow: 'hidden', marginBottom: '0.65rem',
      boxShadow: isToday ? `0 2px 8px ${C.orange}18` : '0 1px 4px rgba(0,0,0,0.05)',
    }}>
      <button
        onClick={() => setOpen(p => !p)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', padding: '0.9rem 1.1rem', background: 'none', border: 'none', cursor: 'pointer', gap: '0.75rem', textAlign: 'left' }}
      >
        <div style={{ width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0, background: entry.shift === 'brunch' ? C.orange : C.navy }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: '0.88rem', color: C.charcoal, textTransform: 'capitalize' }}>{entry.shift}</span>
            <span style={{ fontSize: '0.78rem', color: C.mid }}>— {entry.manager_name}</span>
            {isToday && (
              <span style={{ fontSize: '0.67rem', fontWeight: 700, color: C.orange, background: `${C.orange}18`, padding: '0.1rem 0.4rem', borderRadius: '999px' }}>Today</span>
            )}
          </div>
          <div style={{ fontSize: '0.72rem', color: C.mid, marginTop: '0.08rem' }}>
            {formatDate(entry.date)}
            {entry.submitted_at && <span style={{ marginLeft: '0.4rem' }}>· {formatTime(entry.submitted_at)}</span>}
            <span style={{ marginLeft: '0.5rem', color: filled.length === KITCHEN_PROMPTS.length ? C.green : C.mid }}>
              {filled.length}/{KITCHEN_PROMPTS.length} fields
            </span>
          </div>
        </div>
        {open ? <ChevronUp size={14} style={{ color: C.mid, flexShrink: 0 }} /> : <ChevronDown size={14} style={{ color: C.mid, flexShrink: 0 }} />}
      </button>

      {open && (
        <div style={{ borderTop: `1px solid ${C.border}`, padding: '0.85rem 1.1rem', display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
          {KITCHEN_PROMPTS.map(({ key, label }) =>
            entry[key] ? (
              <div key={key}>
                <span style={{ fontSize: '0.67rem', fontWeight: 700, color: C.orange, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</span>
                <p style={{ margin: '0.15rem 0 0', fontSize: '0.85rem', color: C.charcoal, lineHeight: 1.55 }}>{entry[key]}</p>
              </div>
            ) : (
              <div key={key} style={{ opacity: 0.38 }}>
                <span style={{ fontSize: '0.67rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</span>
                <p style={{ margin: '0.15rem 0 0', fontSize: '0.82rem', color: C.mid, fontStyle: 'italic' }}>Not filled in</p>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}

// ─── Today's status bar ───────────────────────────────────────────────────────
function TodayStatus({ todayEntries, activeShift, onShiftChange }) {
  return (
    <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem' }}>
      {['brunch', 'dinner'].map(s => {
        const done     = todayEntries.some(e => e.shift === s);
        const isActive = activeShift === s;
        return (
          <button
            key={s}
            onClick={() => onShiftChange(s)}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
              padding: '0.55rem 0.5rem', borderRadius: '10px',
              border: `2px solid ${isActive ? C.orange : done ? `${C.green}66` : C.border}`,
              background: isActive ? C.orange : done ? C.greenLight : C.white,
              color: isActive ? C.white : done ? C.green : C.mid,
              fontSize: '0.82rem', fontWeight: isActive ? 700 : 600,
              cursor: 'pointer', transition: 'all 0.15s',
            }}
          >
            {done && !isActive && <Check size={13} strokeWidth={2.5} />}
            <span style={{ textTransform: 'capitalize' }}>{s}</span>
            {done  && !isActive && <span style={{ fontSize: '0.68rem', fontWeight: 700 }}>done</span>}
            {!done && !isActive && <span style={{ fontSize: '0.68rem', color: C.mid }}>pending</span>}
          </button>
        );
      })}
    </div>
  );
}

// ─── Kitchen form ─────────────────────────────────────────────────────────────
function KitchenForm({ venue, shift, existingForShift, currentUser, isGenericUser, onSubmitted }) {
  const autoName = !isGenericUser && currentUser?.name ? currentUser.name : null;
  const [managerName,   setManagerName]   = useState(autoName || '');
  const [fields,        setFields]        = useState({});
  const [submitting,    setSubmitting]    = useState(false);
  const [error,         setError]         = useState('');
  const [showConfirm,   setShowConfirm]   = useState(false);
  const [showMaintModal, setShowMaintModal] = useState(false);
  const [maintLogged,   setMaintLogged]   = useState([]);

  const alreadySubmitted = existingForShift.length > 0;
  const filledCount      = KITCHEN_PROMPTS.filter(p => fields[p.key]?.trim()).length;
  const canSubmit        = managerName.trim().length > 0;
  const vi               = VENUE_IDENTITY[venue] || {};

  async function doSubmit() {
    setSubmitting(true);
    setError('');
    const { data, error: dbErr } = await supabase.from('kitchen_shift_log').insert({
      venue,
      shift,
      date:         getToday(),
      submitted_at: new Date().toISOString(),
      submitted_by: currentUser?.id || null,
      manager_name: managerName.trim(),
      ...Object.fromEntries(
        KITCHEN_PROMPTS.map(p => [p.key, fields[p.key]?.trim() || null])
      ),
    }).select().single();
    setSubmitting(false);
    if (dbErr) { setError('Failed to submit. Try again.'); setShowConfirm(false); return; }
    if (data && onSubmitted) onSubmitted(data);
    setFields({});
    setShowConfirm(false);
  }

  return (
    <>
      {showConfirm && (
        <ConfirmModal
          shift={shift}
          venue={venue}
          managerName={managerName}
          filledCount={filledCount}
          onConfirm={doSubmit}
          onCancel={() => setShowConfirm(false)}
          submitting={submitting}
        />
      )}

      {showMaintModal && (
        <MaintenanceModal
          venue={venue}
          currentUser={currentUser}
          onClose={() => setShowMaintModal(false)}
          onCreated={task => setMaintLogged(prev => [...prev, task])}
        />
      )}

      <div style={{ background: C.white, borderRadius: '12px', border: `1.5px solid ${vi.border || C.border}`, padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', marginBottom: '1rem' }}>

        {/* Context banner */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', padding: '0.55rem 0.85rem', background: vi.bg || C.cream, borderRadius: '8px', border: `1px solid ${vi.border || C.border}` }}>
          <span style={{ fontWeight: 800, fontSize: '0.88rem', color: vi.text || C.navy }}>{venue}</span>
          <span style={{ color: vi.border || C.border, fontWeight: 300 }}>·</span>
          <span style={{ fontSize: '0.82rem', color: vi.text || C.navy, textTransform: 'capitalize', fontWeight: 600 }}>{shift} report</span>
          <span style={{ color: vi.border || C.border, fontWeight: 300 }}>·</span>
          <span style={{ fontSize: '0.78rem', color: C.mid }}>{new Date().toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
        </div>

        {/* Already submitted warning */}
        {alreadySubmitted && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 0.85rem', background: C.amberLight, border: `1px solid ${C.amber}44`, borderRadius: '8px', marginBottom: '1rem', fontSize: '0.8rem', color: C.amber, fontWeight: 600 }}>
            <AlertTriangle size={13} />
            A {shift} report already exists for today ({existingForShift[0]?.manager_name}, {formatTime(existingForShift[0]?.submitted_at)}). Submitting again will add a duplicate.
          </div>
        )}

        {/* Name */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', fontSize: '0.69rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.35rem' }}>
            {isGenericUser ? 'Your name *' : 'Signed as'}
          </label>
          {isGenericUser ? (
            <input
              value={managerName}
              onChange={e => setManagerName(e.target.value)}
              placeholder="Enter your name to submit"
              style={{ width: '100%', padding: '0.5rem 0.7rem', border: `1.5px solid ${managerName.trim() ? C.border : C.orange + '88'}`, borderRadius: '8px', fontSize: '0.88rem', color: C.charcoal, background: C.white, boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' }}
            />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', padding: '0.45rem 0.7rem', background: C.cream, borderRadius: '8px', border: `1px solid ${C.border}` }}>
              <Lock size={12} color={C.mid} />
              <span style={{ fontSize: '0.88rem', fontWeight: 600, color: C.charcoal }}>{autoName}</span>
            </div>
          )}
        </div>

        {/* Prompt fields */}
        {managerName.trim() && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem', marginBottom: '1rem' }}>
            {KITCHEN_PROMPTS.map(({ key, label, placeholder }) => (
              <div key={key}>
                <label style={{ display: 'block', fontSize: '0.69rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.25rem' }}>
                  {label}
                </label>
                <textarea
                  value={fields[key] || ''}
                  onChange={e => setFields(prev => ({ ...prev, [key]: e.target.value }))}
                  placeholder={placeholder}
                  rows={2}
                  style={{ width: '100%', padding: '0.42rem 0.6rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.84rem', resize: 'vertical', boxSizing: 'border-box', color: C.charcoal, background: fields[key] ? C.white : C.cream, fontFamily: 'inherit' }}
                />
                {/* Maintenance escalation button — only shown under Equipment field */}
                {key === 'equipment_notes' && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <button
                      onClick={() => setShowMaintModal(true)}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.3rem 0.85rem', borderRadius: '999px', border: `1.5px solid ${C.amber}88`, background: C.amberLight, color: C.amber, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}
                    >
                      <Plus size={12} /> Log maintenance issue
                    </button>
                    {maintLogged.length > 0 && (
                      <div style={{ marginTop: '0.4rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        {maintLogged.map(t => (
                          <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.75rem', color: C.green }}>
                            <Check size={11} strokeWidth={2.5} />
                            <span>Logged: <strong>{t.title}</strong> ({t.priority})</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Fridge temps section */}
        {managerName.trim() && (
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.65rem' }}>
              <div style={{ flex: 1, height: '1px', background: C.border }} />
              <span style={{ fontSize: '0.67rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>Fridge Temps</span>
              <div style={{ flex: 1, height: '1px', background: C.border }} />
            </div>
            <FridgeTempsSection
              venue={venue}
              dept="kitchen"
              currentUser={currentUser}
            />
          </div>
        )}

        {error && (
          <p style={{ margin: '0 0 0.65rem', fontSize: '0.8rem', color: C.red, fontWeight: 500 }}>{error}</p>
        )}

        <div style={{ display: 'flex', gap: '0.65rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => setShowConfirm(true)}
            disabled={!canSubmit}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              padding: '0.5rem 1.4rem',
              background: canSubmit ? C.orange : C.border,
              color: C.white, border: 'none', borderRadius: '8px',
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              fontWeight: 700, fontSize: '0.88rem',
              boxShadow: canSubmit ? `0 2px 8px ${C.orange}44` : 'none',
              transition: 'all 0.15s',
            }}
          >
            <Send size={14} />
            Submit {shift.charAt(0).toUpperCase() + shift.slice(1)} Report
          </button>
          {!canSubmit && isGenericUser && (
            <span style={{ fontSize: '0.76rem', color: C.mid }}>Enter your name to submit</span>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Venue pills ──────────────────────────────────────────────────────────────
function VenuePills({ venues, active, onChange }) {
  if (venues.length <= 1) return null;
  return (
    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
      {venues.map(v => {
        const vi = VENUE_IDENTITY[v] || {};
        const isActive = active === v;
        return (
          <button key={v} onClick={() => onChange(v)} style={{
            padding: '0.35rem 1rem', borderRadius: '999px',
            border: `2px solid ${isActive ? vi.border || C.navy : C.border}`,
            background: isActive ? vi.bg || C.cream : C.white,
            color: isActive ? vi.text || C.navy : C.mid,
            fontSize: '0.82rem', cursor: 'pointer', fontWeight: isActive ? 700 : 400,
            transition: 'all 0.15s',
          }}>{vi.short || v}</button>
        );
      })}
    </div>
  );
}

// ─── Main KitchenShifty page ──────────────────────────────────────────────────
export default function KitchenShifty({ currentUser }) {
  const userRole      = currentUser?.role || '';
  const isGenericUser = currentUser?.is_generic === true;

  // Kitchen role locked to their venue; admin/manager/supervisor gated by profile venue too
  const isElevated = ['admin', 'manager', 'supervisor'].includes(userRole);
  const isKitchen  = userRole === 'kitchen';

  let accessibleVenues = [];
  if (isElevated) {
    accessibleVenues = VENUES_WITH_KITCHEN.filter(v => !currentUser?.venue || v === currentUser.venue);
  } else if (isKitchen) {
    const v = currentUser?.venue;
    accessibleVenues = VENUES_WITH_KITCHEN.includes(v) ? [v] : VENUES_WITH_KITCHEN;
  }

  const [activeVenue,  setActiveVenue]  = useState(accessibleVenues[0] || '');
  const [activeShift,  setActiveShift]  = useState('brunch');
  const [log,          setLog]          = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [submitted,    setSubmitted]    = useState(false);
  const [filterShift,  setFilterShift]  = useState('all');

  useEffect(() => {
    if (!activeVenue) return;
    setLoading(true);
    supabase.from('kitchen_shift_log')
      .select('*')
      .eq('venue', activeVenue)
      .order('submitted_at', { ascending: false })
      .limit(60)
      .then(({ data }) => { setLog(data || []); setLoading(false); });
  }, [activeVenue]);

  if (!ALLOWED_ROLES.includes(userRole)) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: C.mid, fontSize: '0.9rem' }}>
        Kitchen shift reports are only available to kitchen staff, supervisors, managers and admins.
      </div>
    );
  }

  if (!activeVenue) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: C.mid, fontSize: '0.9rem' }}>
        No kitchen venues accessible for your account.
      </div>
    );
  }

  function handleSubmitted(newEntry) {
    setLog(prev => [newEntry, ...prev]);
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3500);
  }

  const todayEntries     = log.filter(e => e.date === getToday());
  const historyEntries   = log.filter(e => e.date !== getToday());
  const filteredHistory  = filterShift === 'all'
    ? historyEntries
    : historyEntries.filter(e => e.shift === filterShift);

  const existingForShift = todayEntries.filter(e => e.shift === activeShift);
  const vi               = VENUE_IDENTITY[activeVenue] || {};

  return (
    <div style={{ padding: '1.25rem', maxWidth: '720px', margin: '0 auto', background: C.cream, minHeight: '100vh' }}>

      {/* Header */}
      <div style={{ marginBottom: '1.25rem' }}>
        <h2 style={{ margin: '0 0 0.18rem', color: C.charcoal, fontSize: '1.2rem', fontWeight: 700 }}>
          Kitchen Shifty
          <span style={{ fontWeight: 400, fontSize: '0.78rem', color: C.mid, marginLeft: '0.55rem' }}>end of shift report</span>
        </h2>
        <p style={{ margin: 0, fontSize: '0.8rem', color: C.mid }}>
          {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
      </div>

      {/* Venue pills */}
      <VenuePills
        venues={accessibleVenues}
        active={activeVenue}
        onChange={v => { setActiveVenue(v); setLog([]); setLoading(true); }}
      />

      {/* Success flash */}
      {submitted && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.65rem 1rem', background: C.greenLight, border: `1px solid ${C.green}44`, borderRadius: '9px', marginBottom: '1rem', fontSize: '0.84rem', color: C.green, fontWeight: 600 }}>
          <Check size={14} /> Kitchen shift report submitted ✓
        </div>
      )}

      {/* Shift selector */}
      <div style={{ marginBottom: '0.75rem' }}>
        <p style={{ margin: '0 0 0.5rem', fontSize: '0.7rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          New Report — <span style={{ color: vi.text || C.navy }}>{vi.short || activeVenue}</span>
        </p>
        <TodayStatus
          todayEntries={todayEntries}
          activeShift={activeShift}
          onShiftChange={setActiveShift}
        />
      </div>

      {/* Form */}
      <KitchenForm
        key={`${activeVenue}-${activeShift}`}
        venue={activeVenue}
        shift={activeShift}
        existingForShift={existingForShift}
        currentUser={currentUser}
        isGenericUser={isGenericUser}
        onSubmitted={handleSubmitted}
      />

      {/* Today's reports */}
      {todayEntries.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <p style={{ margin: '0 0 0.65rem', fontSize: '0.7rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Today's Reports
          </p>
          {todayEntries.map(entry => (
            <KitchenCard key={entry.id} entry={entry} defaultOpen={todayEntries.length === 1} />
          ))}
        </div>
      )}

      {/* History */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.65rem', flexWrap: 'wrap', gap: '0.4rem' }}>
          <p style={{ margin: 0, fontSize: '0.7rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em' }}>History</p>
          <div style={{ display: 'flex', gap: '0.3rem' }}>
            {['all', 'brunch', 'dinner'].map(f => (
              <button key={f} onClick={() => setFilterShift(f)} style={{
                padding: '0.22rem 0.65rem', borderRadius: '999px',
                border: `1px solid ${filterShift === f ? C.orange : C.border}`,
                background: filterShift === f ? C.orange : C.white,
                color: filterShift === f ? C.white : C.mid,
                fontSize: '0.72rem', cursor: 'pointer', fontWeight: filterShift === f ? 600 : 400,
                textTransform: 'capitalize',
              }}>{f}</button>
            ))}
          </div>
        </div>

        {loading && <p style={{ color: C.mid, fontSize: '0.84rem' }}>Loading…</p>}
        {!loading && filteredHistory.length === 0 && (
          <p style={{ color: C.mid, fontSize: '0.84rem' }}>No past reports{filterShift !== 'all' ? ` for ${filterShift}` : ''}.</p>
        )}
        {filteredHistory.map(entry => (
          <KitchenCard key={entry.id} entry={entry} />
        ))}
      </div>
    </div>
  );
}